import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { useLang } from '../lib/i18n';
import { dayShort, hhmm, istMidnight } from '../lib/format';
import { savedOrders } from '../lib/orders';
import { AppHeader, Loading } from '../components/AppHeader';
import { Ico, Poster } from '../components/brand';
import type { EventContent } from '../lib/types';

interface Row {
  id: string;
  no: string;
  accessKey?: string;
  startsAt: number;
  sessionId: string;
  event: EventContent;
  venue: string;
  count: number;
  seats: string[];
}

export function MyTickets() {
  const nav = useNavigate();
  const { tx, lang } = useLang();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [now, setNow] = useState(Date.now());
  const [tab, setTab] = useState<'up' | 'past'>('up');
  useEffect(() => {
    api<{ now: number; orders: Row[] }>('/me/tickets').then(
      (r) => {
        // merge tickets saved on this device ("Cüzdana ekle") that belong to another browser profile/cookie
        const local = Object.values(savedOrders())
          .filter((o) => !r.orders.some((x) => x.id === o.id))
          .map((o) => ({ id: o.id, no: o.no, accessKey: o.accessKey, startsAt: o.session.startsAt, sessionId: o.session.id, event: o.event, venue: o.venue.name, count: o.tickets.length, seats: o.tickets.map((t) => t.label) }));
        setRows([...r.orders, ...local].sort((a, b) => a.startsAt - b.startsAt));
        setNow(r.now);
      },
      () => {
        const local = Object.values(savedOrders()).map((o) => ({ id: o.id, no: o.no, accessKey: o.accessKey, startsAt: o.session.startsAt, sessionId: o.session.id, event: o.event, venue: o.venue.name, count: o.tickets.length, seats: o.tickets.map((t) => t.label) }));
        setRows(local);
      },
    );
  }, []);
  if (!rows) return <Loading />;
  const up = rows.filter((r) => r.startsAt > now - 3 * 3600_000);
  const past = rows.filter((r) => r.startsAt <= now - 3 * 3600_000).reverse();
  const weekEnd = istMidnight(now) + 7 * 86400_000;
  const list = tab === 'up' ? up : past;
  const short = (s: string) => s.replace(' sırası · ', ' ').replace('Parter · ', '').replace('Balkon · ', 'Balkon ');

  return (
    <div className="scroll">
      <AppHeader cur="mytickets" />
      <div className="wrap" style={{ maxWidth: 820, paddingBottom: 48 }}>
        <h1 className="d-xl" style={{ margin: '16px 0 18px' }}>
          {tx('Biletlerim', 'My tickets')}
        </h1>
        <div className="chips" role="tablist">
          <button type="button" className="chip" role="tab" aria-selected={tab === 'up'} aria-pressed={tab === 'up'} onClick={() => setTab('up')}>
            {tx('Yaklaşan', 'Upcoming')} · {up.length}
          </button>
          <button type="button" className="chip" role="tab" aria-selected={tab === 'past'} aria-pressed={tab === 'past'} onClick={() => setTab('past')}>
            {tx('Geçmiş', 'Past')} · {past.length}
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 }}>
          {list.map((r, i) => (
            <button key={r.id} type="button" className={`tk-row ${tab === 'past' ? 'past' : ''}`} onClick={() => nav(`/bilet/${r.id}${r.accessKey ? `?k=${r.accessKey}` : ''}`)}>
              <Poster poster={r.event.poster} title={r.event.short || r.event.title} />
              <div>
                <div className={`when-big ${i === 0 && tab === 'up' ? '' : 'muted'}`} style={i === 0 && tab === 'up' ? { color: 'var(--cobalt-ink)', fontWeight: 600 } : undefined}>
                  {i === 0 && tab === 'up' && r.startsAt < weekEnd ? tx('Bu hafta · ', 'This week · ') : ''}
                  {dayShort(r.startsAt, lang)} · {hhmm(r.startsAt)}
                </div>
                <h4>{r.event.title}</h4>
                <div className="t-s muted">
                  {r.venue} · {r.count} {tx('bilet', r.count === 1 ? 'ticket' : 'tickets')} · {r.seats.map(short).join(', ')}
                </div>
              </div>
              {tab === 'up' ? <Ico n="chev" /> : <span className="badge">{tx('Geçti', 'Past')}</span>}
            </button>
          ))}
          {!list.length && (
            <div className="panel" style={{ textAlign: 'center', padding: '32px 20px' }}>
              <div className="d-s">{tab === 'up' ? tx('Yaklaşan biletin yok', 'No upcoming tickets') : tx('Geçmiş biletin yok', 'No past tickets')}</div>
              <p className="t-s muted" style={{ margin: '6px 0 14px' }}>
                {tx('Bu cihazdan aldığın biletler burada görünür.', 'Tickets bought on this device show up here.')}
              </p>
              <button type="button" className="btn btn-primary" onClick={() => nav('/')}>
                {tx('Etkinliklere bak', 'Browse events')}
              </button>
            </div>
          )}
        </div>
        <div className="panel-flat" style={{ marginTop: 24, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Ico n="info" />
          <p className="t-s" style={{ margin: 0 }}>
            {lang === 'en' ? (
              <>
                To pass a ticket to a friend, open it and tap <b>Share</b>. The link carries a private key; anyone with it can show the QR code.
              </>
            ) : (
              <>
                Bileti bir arkadaşına göndermek için bileti aç ve <b>Paylaş</b>’a dokun. Bağlantı gizli bir anahtar taşır; bağlantıya sahip olan QR kodu gösterebilir.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
